import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import API_CONFIG, { buildApiUrl, buildApiUrlWithParams } from '../config/api';

const RegisterStudents = () => {
    const [events, setEvents] = useState([]);
    const [students, setStudents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState('');
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showRegistrationModal, setShowRegistrationModal] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [specificData, setSpecificData] = useState({});
    const [eventFee, setEventFee] = useState(0);
    const [isPaid, setIsPaid] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const navigate = useNavigate();
    const [groups, setGroups] = useState("");
    const [studentFilters, setStudentFilters] = useState({
        beltRank: '',
        nameSearch: '',
        ageRange: ''
    });

    useEffect(() => {
        fetchEvents();
        fetchStudents();
    }, []);

    useEffect(() => {
        if (selectedEvent) {
            fetchRegistrations(selectedEvent);
        }
    }, [selectedEvent]);

    const fetchEvents = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                console.error("No authentication token found.");
                return;
            } else {
                const decoded = JSON.parse(atob(token.split(".")[1]));
                setGroups(decoded["cognito:groups"]);
            }
            const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.EVENTS), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) throw new Error('Failed to fetch events');
            const data = await response.json();
            setEvents(data.filter(event => event.active));
        } catch (err) {
            setError(err.message);
        }
    };

    const fetchStudents = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                console.error("No authentication token found.");
                return;
            } else {
                const decoded = JSON.parse(atob(token.split(".")[1]));
                setGroups(decoded["cognito:groups"]);
            }
            const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.STUDENTS), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) throw new Error('Failed to fetch students');
            const data = await response.json();
            setStudents(data);
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const fetchRegistrations = async (eventId) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                console.error("No authentication token found.");
                return;
            } else {
                const decoded = JSON.parse(atob(token.split(".")[1]));
                setGroups(decoded["cognito:groups"]);
            }
            const response = await fetch(buildApiUrlWithParams(API_CONFIG.ENDPOINTS.EVENT_REGISTRATIONS, eventId), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) throw new Error('Failed to fetch registrations');
            const data = await response.json();
            setRegistrations(data);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleEventChange = (e) => {
        setSelectedEvent(e.target.value);
    };

    const handleRegisterClick = (student) => {
        setSelectedStudent(student);
        const selectedEventData = events.find(e => e.event_id === parseInt(selectedEvent));
        if (selectedEventData && selectedEventData.specific_data_schema) {
            const schema = JSON.parse(selectedEventData.specific_data_schema);
            const initialData = {};
            schema.properties.forEach(prop => {
                initialData[prop.name] = '';
            });
            setSpecificData(initialData);
        }
        setShowRegistrationModal(true);
    };

    const handleSpecificDataChange = (name, value) => {
        setSpecificData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleRegister = async () => {
        try {
            if (!selectedEvent || !selectedStudent) {
                setError('Please select both an event and a student');
                return;
            }

            const registrationData = {
                event_id: parseInt(selectedEvent),
                student_id: selectedStudent.student_id,
                event_fee: eventFee || 0,  // Ensure event_fee is never null
                event_specific_data: specificData,
                is_paid: isPaid
            };

            console.log('Sending registration data:', registrationData);  // Debug log

            const token = localStorage.getItem('token');
            if (!token) {
                setError('No authentication token found');
                return;
            }

            const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.EVENT_REGISTRATIONS_SAVE), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(registrationData)
            });

            const responseData = await response.json();  // Get the response data
            console.log('Registration response:', responseData);  // Debug log

            if (!response.ok) {
                throw new Error(responseData.error || 'Failed to register student');
            }
            
            // Refresh registrations
            await fetchRegistrations(selectedEvent);
            
            // Reset and close modal
            setShowRegistrationModal(false);
            setSelectedStudent(null);
            setSpecificData({});
            setEventFee(0);
            setIsPaid(false);
            setError(null);  // Clear any previous errors
        } catch (err) {
            console.error('Registration error:', err);  // Debug log
            setError(err.message || 'Failed to register student');
        }
    };

    const handleUnregister = async (registrationId) => {
        try {
            const response = await fetch(buildApiUrlWithParams(API_CONFIG.ENDPOINTS.EVENT_REGISTRATION_DELETE, registrationId), {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) throw new Error('Failed to unregister student');
            
            // Refresh registrations
            fetchRegistrations(selectedEvent);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleStudentFilterChange = (e) => {
        const { name, value } = e.target;
        setStudentFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const getAgeRange = (birthDate) => {
        const today = new Date();
        const birth = new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
            age--;
        }

        if (age <= 6) return 'Babies';
        if (age >= 7 && age <= 12) return 'Juniors';
        if (age >= 13 && age <= 17) return 'Teens';
        return 'Adults';
    };

    const filterStudents = (students) => {
        return students.filter(student => {
            // Filter by belt rank
            if (studentFilters.beltRank && student.current_rank !== studentFilters.beltRank) {
                return false;
            }

            // Filter by name search
            if (studentFilters.nameSearch) {
                const searchTerm = studentFilters.nameSearch.toLowerCase();
                const fullName = `${student.first_name} ${student.last_name}`.toLowerCase();
                if (!fullName.includes(searchTerm)) {
                    return false;
                }
            }

            // Filter by age range
            if (studentFilters.ageRange) {
                const studentAgeRange = getAgeRange(student.date_of_birth);
                if (studentAgeRange !== studentFilters.ageRange) {
                    return false;
                }
            }

            return true;
        });
    };

    const handleBulkUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        
        if (!selectedEvent) {
            setUploadError('Please select an event first');
            return;
        }

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const csvContent = e.target.result;
                const token = localStorage.getItem('token');
                
                const response = await fetch(buildApiUrlWithParams(API_CONFIG.ENDPOINTS.EVENT_BULK, selectedEvent), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ csv_content: csvContent })
                });

                if (!response.ok) {
                    throw new Error('Failed to upload registrations');
                }

                setUploadSuccess(true);
                setUploadError(null);
                // Refresh registrations after successful upload
                fetchRegistrations(selectedEvent);
                
                // Reset the file input
                event.target.value = '';
                
                // Clear success message after 3 seconds
                setTimeout(() => setUploadSuccess(false), 3000);
            } catch (err) {
                setUploadError(err.message);
                setUploadSuccess(false);
            }
        };

        reader.readAsText(file);
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    const registeredStudentIds = registrations.map(r => r.student_id);
    const unregisteredStudents = students.filter(s => !registeredStudentIds.includes(s.student_id));
    const filteredUnregisteredStudents = filterStudents(unregisteredStudents);
    const selectedEventData = events.find(e => e.event_id === parseInt(selectedEvent));
    const eventTypeSchema = selectedEventData ? 
        JSON.parse(selectedEventData.specific_data_schema || '{"properties":[]}') : 
        { properties: [] };

    return (
        <div className="container mt-4">
            <Navbar groups={groups} />
            <h2>Register Students for Events</h2>
            
            {error && (
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            )}

            <div className="mb-4">
                <label className="form-label">Select Event</label>
                <select 
                    className="form-select" 
                    value={selectedEvent} 
                    onChange={handleEventChange}
                >
                    <option value="">Select an Event</option>
                    {events.map(event => (
                        <option key={event.event_id} value={event.event_id}>
                            {event.event_name} - {event.event_date}
                        </option>
                    ))}
                </select>
            </div>

            {selectedEvent && (
                <>
                    <div className="row">
                        <div className="col-md-6">
                            <h3>Available Students</h3>
                            
                            <div className="card mb-3">
                                <div className="card-body">
                                    <h5 className="card-title">Filter Students</h5>
                                    <div className="row">
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Belt Rank</label>
                                            <select
                                                className="form-select"
                                                name="beltRank"
                                                value={studentFilters.beltRank}
                                                onChange={handleStudentFilterChange}
                                            >
                                                <option value="">All Ranks</option>
                                                <option value="WHITE">White</option>
                                                <option value="YELLOW">Yellow</option>
                                                <option value="GREEN">Green</option>
                                                <option value="BLUE">Blue</option>
                                                <option value="RED">Red</option>
                                                <option value="PREBLACK">Pre-Black</option>
                                                <option value="BLACK">Black</option>
                                            </select>
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Name Search</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="nameSearch"
                                                value={studentFilters.nameSearch}
                                                onChange={handleStudentFilterChange}
                                                placeholder="Search by name..."
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Age Range</label>
                                            <select
                                                className="form-select"
                                                name="ageRange"
                                                value={studentFilters.ageRange}
                                                onChange={handleStudentFilterChange}
                                            >
                                                <option value="">All Ages</option>
                                                <option value="Babies">Babies</option>
                                                <option value="Juniors">Juniors</option>
                                                <option value="Teens">Teens</option>
                                                <option value="Adults">Adults</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="table-responsive">
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Belt Rank</th>
                                            <th>Age</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredUnregisteredStudents.map(student => (
                                            <tr key={student.student_id}>
                                                <td>{student.first_name} {student.last_name}</td>
                                                <td>{student.current_rank}</td>
                                                <td>{getAgeRange(student.date_of_birth)}</td>
                                                <td>
                                                    <button 
                                                        className="btn btn-primary btn-sm"
                                                        onClick={() => handleRegisterClick(student)}
                                                    >
                                                        Register
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h3>Registered Students</h3>
                                <div>
                                    <input
                                        type="file"
                                        accept=".csv"
                                        onChange={handleBulkUpload}
                                        style={{ display: 'none' }}
                                        id="csvUpload"
                                    />
                                    <label htmlFor="csvUpload" className="btn btn-primary">
                                        Upload CSV
                                    </label>
                                </div>
                            </div>
                            {uploadError && (
                                <div className="alert alert-danger" role="alert">
                                    {uploadError}
                                </div>
                            )}
                            {uploadSuccess && (
                                <div className="alert alert-success" role="alert">
                                    Registrations uploaded successfully!
                                </div>
                            )}
                            <div className="table-responsive">
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Fee</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {registrations.map(registration => (
                                            <tr key={registration.event_registration_id}>
                                                <td>{registration.student_name}</td>
                                                <td>${registration.event_fee}</td>
                                                <td>
                                                    <button 
                                                        className="btn btn-danger btn-sm"
                                                        onClick={() => handleUnregister(registration.event_registration_id)}
                                                    >
                                                        Unregister
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Registration Modal */}
            {showRegistrationModal && selectedStudent && (
                <div className="modal show d-block" tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Register {selectedStudent.first_name} {selectedStudent.last_name}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowRegistrationModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Event Fee</label>
                                    <input 
                                        type="number" 
                                        className="form-control" 
                                        value={eventFee} 
                                        onChange={(e) => setEventFee(parseFloat(e.target.value))}
                                        min="0"
                                        step="0.01"
                                    />
                                </div>
                                <div className="mb-3">
                                    <div className="form-check">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="isPaid"
                                            checked={isPaid}
                                            onChange={(e) => setIsPaid(e.target.checked)}
                                        />
                                        <label className="form-check-label" htmlFor="isPaid">
                                            Mark fee as paid
                                        </label>
                                    </div>
                                </div>

                                {eventTypeSchema.properties.map(prop => (
                                    <div key={prop.name} className="mb-3">
                                        <label className="form-label">{prop.title}</label>
                                        {prop.type === 'list' ? (
                                            <select 
                                                className="form-select"
                                                value={specificData[prop.name] || ''}
                                                onChange={(e) => handleSpecificDataChange(prop.name, e.target.value)}
                                                required
                                            >
                                                <option value="">Select {prop.title}</option>
                                                {prop.values.split(',').map(value => (
                                                    <option key={value} value={value.trim()}>
                                                        {value.trim()}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input 
                                                type={prop.type === 'integer' ? 'number' : 'text'}
                                                className="form-control"
                                                value={specificData[prop.name] || ''}
                                                onChange={(e) => handleSpecificDataChange(prop.name, e.target.value)}
                                                required
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowRegistrationModal(false)}>Cancel</button>
                                <button type="button" className="btn btn-primary" onClick={handleRegister}>Register</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RegisterStudents; 